import React, { useState, useEffect } from 'react';
import { 
  Leaf, TrendingUp, CheckCircle, Clock, Globe, 
  ChevronDown, ArrowRight, Shield, FileText, Award,
  DollarSign, Calendar, Zap, Phone, Building2,
  Target, Sparkles, TreePine, Droplets
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';

const CreditosVerdesPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const greenCreditTypes = [
    {
      icon: Zap,
      title: 'Crédito Solar',
      description: 'Financiamiento exclusivo para sistemas fotovoltaicos residenciales, comerciales e industriales.',
      benefits: ['Tasa preferencial', 'Hasta 60 meses', 'Sin garantía adicional'],
      color: 'emerald'
    },
    {
      icon: TreePine,
      title: 'Crédito Eficiencia Energética',
      description: 'Para proyectos que reduzcan el consumo energético: iluminación LED, climatización eficiente, etc.',
      benefits: ['Incluye auditoría', 'ROI garantizado', 'Asesoría técnica'],
      color: 'green'
    },
    {
      icon: Droplets,
      title: 'Crédito Agua y Residuos',
      description: 'Sistemas de tratamiento de agua, reciclaje y gestión de residuos para empresas.',
      benefits: ['Cumplimiento ambiental', 'Certificación ISO', 'Imagen corporativa'],
      color: 'blue'
    },
    {
      icon: Globe,
      title: 'Crédito Carbono Neutro',
      description: 'Proyectos integrales para alcanzar la neutralidad de carbono en tu empresa.',
      benefits: ['Certificación Huella Cero', 'Marketing verde', 'Beneficios fiscales'],
      color: 'teal'
    }
  ];

  const institutions = [
    {
      name: 'Banco de Desarrollo del Paraguay (BDP)',
      type: 'Banco de desarrollo',
      speciality: 'Líneas especiales para PyMEs verdes',
      rate: 'Desde 8% anual'
    },
    {
      name: 'Agencia Financiera de Desarrollo (AFD)',
      type: 'Banca de segundo piso',
      speciality: 'Fondos internacionales para energía limpia',
      rate: 'Desde 6% anual'
    },
    {
      name: 'IFC (Banco Mundial)',
      type: 'Organismo multilateral',
      speciality: 'Proyectos de gran escala',
      rate: 'Condiciones especiales'
    },
    {
      name: 'BID Invest',
      type: 'Banca de desarrollo',
      speciality: 'Financiamiento climático',
      rate: 'Tasas preferenciales'
    }
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Tasas Preferenciales',
      description: 'Los créditos verdes tienen tasas de interés más bajas que los créditos tradicionales, gracias a fondos internacionales de clima.'
    },
    {
      icon: Calendar,
      title: 'Plazos Extendidos',
      description: 'Períodos de gracia y plazos más largos para que la inversión genere retorno antes de comenzar a pagar.'
    },
    {
      icon: Shield,
      title: 'Garantías Flexibles',
      description: 'Requisitos de garantía reducidos. El propio proyecto verde puede servir como colateral.'
    },
    {
      icon: Award,
      title: 'Asistencia Técnica',
      description: 'Muchos programas incluyen asistencia técnica gratuita para diseño y seguimiento del proyecto.'
    },
    {
      icon: Globe,
      title: 'Certificación Ambiental',
      description: 'Acceso a certificaciones reconocidas internacionalmente que mejoran tu imagen corporativa.'
    },
    {
      icon: Target,
      title: 'Impacto Medible',
      description: 'Métricas claras de reducción de CO2, ahorro energético y retorno de inversión.'
    }
  ];

  const processSteps = [
    {
      step: 1,
      title: 'Evaluación del Proyecto',
      description: 'Analizamos tu proyecto para determinar si califica como "verde" según estándares internacionales.',
      icon: FileText
    },
    {
      step: 2,
      title: 'Identificación de Fuentes',
      description: 'Identificamos las líneas de crédito verde más convenientes según el tipo y tamaño del proyecto.',
      icon: Target
    },
    {
      step: 3,
      title: 'Preparación de Documentos',
      description: 'Preparamos toda la documentación técnica y financiera requerida por las instituciones.',
      icon: FileText
    },
    {
      step: 4,
      title: 'Gestión y Seguimiento',
      description: 'Presentamos la solicitud y damos seguimiento hasta la aprobación y desembolso.',
      icon: TrendingUp
    }
  ];

  const faqs = [
    {
      question: '¿Qué es un Crédito Verde?',
      answer: 'Un Crédito Verde es un producto financiero diseñado específicamente para proyectos que tienen un impacto ambiental positivo, como energía renovable, eficiencia energética, o gestión sostenible de recursos. Estos créditos generalmente ofrecen condiciones más favorables que los créditos tradicionales.'
    },
    {
      question: '¿Mi proyecto califica como "verde"?',
      answer: 'Los proyectos de energía solar, eficiencia energética, gestión de agua y residuos, y reducción de emisiones generalmente califican. Zenex evalúa tu proyecto gratuitamente para determinar su elegibilidad.'
    },
    {
      question: '¿Cuánto demora obtener un crédito verde?',
      answer: 'El proceso puede tomar entre 30 y 90 días dependiendo de la complejidad del proyecto y la institución financiera. Proyectos más simples como instalaciones solares residenciales pueden aprobarse más rápido.'
    },
    {
      question: '¿Hay montos mínimos o máximos?',
      answer: 'Varía según la institución y el programa. Algunos fondos están diseñados para PyMEs (desde USD 10,000) mientras que otros financian proyectos de gran escala (millones de dólares).'
    },
    {
      question: '¿Puedo combinar crédito verde con beneficios fiscales?',
      answer: '¡Sí! De hecho, es una estrategia recomendada. La Ley de Energías Renovables ofrece exenciones fiscales que se suman a las ventajas del crédito verde, maximizando el retorno de inversión.'
    },
    {
      question: '¿Zenex cobra por gestionar el crédito verde?',
      answer: 'La evaluación inicial y asesoría son gratuitas. Para proyectos complejos que requieren preparación de documentación especializada, ofrecemos un servicio de consultoría con costos transparentes.'
    }
  ];

  const eligibleProjects = [
    'Instalación de paneles solares',
    'Sistemas de almacenamiento de energía',
    'Iluminación LED y eficiencia energética',
    'Vehículos eléctricos y estaciones de carga',
    'Tratamiento y reciclaje de agua',
    'Gestión de residuos sólidos',
    'Certificación carbono neutral',
    'Construcción sostenible (LEED)'
  ];

  const getColorClasses = (color) => {
    const colors = {
      emerald: 'bg-emerald-100 text-emerald-600 group-hover:bg-emerald-600 group-hover:text-white',
      green: 'bg-green-100 text-green-600 group-hover:bg-green-600 group-hover:text-white',
      blue: 'bg-blue-100 text-blue-600 group-hover:bg-blue-600 group-hover:text-white',
      teal: 'bg-teal-100 text-teal-600 group-hover:bg-teal-600 group-hover:text-white'
    };
    return colors[color] || colors.emerald;
  };

  return (
    <div className="min-h-screen bg-white" data-testid="creditos-verdes-page">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-20 pb-32 bg-gradient-to-br from-emerald-900 via-green-800 to-teal-900 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-20 w-72 h-72 bg-emerald-500/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-teal-400/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex items-center gap-2 text-emerald-400 mb-6">
            <Leaf className="w-5 h-5" />
            <span className="text-sm font-medium tracking-wider uppercase">Financiamiento Sustentable</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Créditos Verdes
            <span className="block text-emerald-400">Financiá el Futuro Sostenible</span>
          </h1>
          
          <p className="text-xl text-zinc-300 max-w-2xl mb-8">
            Accedé a líneas de crédito especiales con tasas preferenciales 
            para proyectos de energía renovable y sostenibilidad.
          </p>
          
          <div className="flex flex-wrap gap-4">
            <a 
              href="https://wa.me/595991344956?text=Hola,%20me%20interesa%20información%20sobre%20créditos%20verdes"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl transition-all duration-300 flex items-center gap-2 shadow-lg shadow-emerald-500/30"
            >
              Consultar Elegibilidad
              <ArrowRight className="w-5 h-5" />
            </a>
            <button 
              onClick={() => navigate('/calculadora')}
              className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl transition-all duration-300 flex items-center gap-2 backdrop-blur-sm"
            >
              <Zap className="w-5 h-5" />
              Calcular Mi Proyecto
            </button>
          </div>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
            {[
              { value: '6%', label: 'Tasa desde (anual)' },
              { value: '10', label: 'Años de plazo' },
              { value: '2', label: 'Años de gracia' },
              { value: '100%', label: 'Financiamiento' }
            ].map((stat, index) => (
              <div key={index} className="text-center p-4 bg-white/5 rounded-xl backdrop-blur-sm">
                <p className="text-3xl font-bold text-emerald-400">{stat.value}</p>
                <p className="text-sm text-zinc-400">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Credit Types Section */}
      <section className="py-20 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Tipos de Créditos Verdes
            </h2>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              Existen diferentes líneas de financiamiento verde según el tipo de proyecto.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {greenCreditTypes.map((credit, index) => {
              const Icon = credit.icon;
              return (
                <div 
                  key={index} 
                  className="p-8 bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 group border border-zinc-100"
                >
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-colors ${getColorClasses(credit.color)}`}>
                    <Icon className="w-7 h-7" />
                  </div>
                  <h3 className="text-2xl font-bold text-zinc-900 mb-3">{credit.title}</h3>
                  <p className="text-zinc-600 mb-6">{credit.description}</p>
                  <ul className="space-y-2">
                    {credit.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-zinc-700">
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Ventajas de los Créditos Verdes
            </h2>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              Los fondos climáticos internacionales permiten ofrecer condiciones únicas.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div 
                  key={index} 
                  className="p-6 bg-gradient-to-br from-emerald-50 to-white rounded-xl hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-zinc-900 mb-2">{benefit.title}</h3>
                  <p className="text-zinc-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Institutions Section */}
      <section className="py-20 bg-zinc-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Instituciones y Fondos Disponibles
            </h2>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              Trabajamos con las principales instituciones que ofrecen líneas de crédito verde en Paraguay.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {institutions.map((inst, index) => (
              <div key={index} className="p-6 bg-zinc-800/50 rounded-xl border border-zinc-700">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold mb-1">{inst.name}</h3>
                    <p className="text-emerald-400 text-sm mb-2">{inst.type}</p>
                    <p className="text-zinc-400 text-sm mb-2">{inst.speciality}</p>
                    <span className="inline-block px-3 py-1 bg-emerald-600/20 text-emerald-400 text-sm rounded-full">
                      {inst.rate}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Eligible Projects */}
      <section className="py-20 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-6">
                Proyectos Elegibles
              </h2>
              <p className="text-lg text-zinc-600 mb-8">
                Estos son algunos de los proyectos que califican para acceder a créditos verdes con tasas preferenciales.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {eligibleProjects.map((project, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm">
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    <span className="text-zinc-700 text-sm">{project}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-emerald-600 to-teal-600 p-8 rounded-2xl text-white">
              <Sparkles className="w-12 h-12 mb-6" />
              <h3 className="text-2xl font-bold mb-4">¿Tu proyecto no está en la lista?</h3>
              <p className="text-emerald-100 mb-6">
                Consultanos de todas formas. Evaluamos cada proyecto individualmente y 
                podemos identificar líneas de financiamiento específicas para tu caso.
              </p>
              <a 
                href="https://wa.me/595991344956?text=Hola,%20quiero%20consultar%20si%20mi%20proyecto%20califica%20para%20crédito%20verde"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-emerald-600 font-semibold rounded-xl hover:bg-emerald-50 transition-all"
              >
                <Phone className="w-5 h-5" />
                Consultar Mi Proyecto
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              ¿Cómo Funciona?
            </h2>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              Te acompañamos en todo el proceso de obtención del crédito verde.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {processSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="text-center relative">
                  {index < processSteps.length - 1 && (
                    <div className="hidden md:block absolute top-6 left-1/2 w-full h-0.5 bg-emerald-200"></div>
                  )}
                  <div className="relative z-10 w-12 h-12 bg-emerald-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold">
                    {step.step}
                  </div>
                  <h3 className="text-lg font-semibold text-zinc-900 mb-2">{step.title}</h3>
                  <p className="text-zinc-600 text-sm">{step.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-20 bg-zinc-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Preguntas Frecuentes
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="border border-zinc-200 rounded-xl overflow-hidden bg-white"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-zinc-50 transition-colors"
                >
                  <span className="font-semibold text-zinc-900 pr-4">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-zinc-500 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5 text-zinc-600">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-emerald-600 to-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Leaf className="w-16 h-16 mx-auto text-white/80 mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Invertí en un futuro sostenible
          </h2>
          <p className="text-xl text-emerald-100 mb-8">
            Te ayudamos a encontrar el crédito verde ideal para tu proyecto.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="https://wa.me/595991344956?text=Hola,%20quiero%20asesoramiento%20sobre%20créditos%20verdes"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-white text-emerald-600 font-semibold rounded-xl hover:bg-emerald-50 transition-all flex items-center gap-2"
            >
              <Phone className="w-5 h-5" />
              Asesoramiento Gratuito
            </a>
            <button 
              onClick={() => navigate('/financiamiento/beneficios')}
              className="px-8 py-4 bg-emerald-800/50 text-white font-semibold rounded-xl hover:bg-emerald-800 transition-all flex items-center gap-2"
            >
              Ver Beneficios Fiscales
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default CreditosVerdesPage;
